import { type FC, useCallback, useEffect, useRef, useState } from 'react'

import { useUnit } from 'effector-react'

import {
  $streamingError,
  getStreamingUrlFx,
} from '@/models/vault/medias/streams.ts'
import { preventDefaultEvent, stopEventPropagation } from '@/utils/helpers.ts'
import { useKeydownHandler } from '@/utils/useKeydownHandler.ts'

export interface Props {
  className?: string
  src: string
  mediaId: number
}

export const VideoPlayer: FC<Props> = ({ className, src, mediaId }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const isLoadingMediaSource = useUnit(getStreamingUrlFx.pending)
  const streamingError = useUnit($streamingError)
  const [play, setPlay] = useState(false)
  const [videoReady, setVideoReady] = useState(false)
  const handleKeyPress = useCallback((event: Event) => {
    if ('code' in event) {
      if (event.code === 'Space') {
        // stop page scrolling
        event.preventDefault()
        setPlay((prevState) => !prevState)
      }
    }
  }, [])

  useKeydownHandler(
    handleKeyPress,
    !!videoRef.current && !isLoadingMediaSource && !streamingError,
  )

  useEffect(() => {
    // reset play\ready flags on src update
    if (src) {
      setPlay(false)
      setVideoReady(false)
    }
  }, [src])

  useEffect(() => {
    if (videoReady) {
      if (play) {
        videoRef.current?.play()
      } else {
        videoRef.current?.pause()
      }
    }
  }, [play, videoReady])

  useEffect(() => {
    const handleVideoReady = () => {
      setVideoReady(true)
    }

    const video = videoRef.current

    if (src && !isLoadingMediaSource) {
      video?.addEventListener('canplay', handleVideoReady)
      video?.load()
    }

    return () => {
      video?.removeEventListener('canplay', handleVideoReady)
    }
  }, [isLoadingMediaSource, src])

  return (
    <video
      onClick={stopEventPropagation}
      autoFocus
      ref={videoRef}
      className={className}
      controls
      controlsList="nodownload"
      onContextMenu={preventDefaultEvent}
      src={src}
      data-media-id={mediaId}
    />
  )
}
